require('intersection-observer');

import panelUpdater from '@remarkablenet/panel-updater';
import { dispatchEventWrapper } from '../event/event';
import { fetchText } from '../fetch-helper/fetch-helper';
import { scrollIntoView } from '../scroll-into-view/scroll-into-view';
import { getOffset } from '../utility-functions/utility-functions';
import { isMobile } from '../mobile-detect/mobile-detect';
import { updateUrl, getScrollToProductId, removeScrollToProductId } from './utility-functions';


export type PageLoaderOptions = {
    context?: string;
    watch?: string[];
    pageContainer?: string;
    item?: string;
    paging?: {
        container: string;
        next: string;
        prev: string;
    };
    activationMethod?: {
        next: string;
        prev: string;
    };
    activeClass?: string;
    ignore?: string;
    method?: string;
    scrollToTop?: boolean;
    saveScrollPosiion?: boolean;
    pagingHandler?: Function | boolean;
    modifyContent?: Function | boolean;
    updateMethod?: Function;
    beforeUpdate?: Function | boolean;
    afterUpdate?: Function | boolean;
    setupScrollClickHandler?: Function | boolean;
    autoPaging?: boolean;
};

// #region Intersection Observer
const observer = new IntersectionObserver(intersectionObserverTriggered, {
    rootMargin: '0px 0px 1500px 0px'
});

const observeVisiblePage = new IntersectionObserver(intersectionObserverVisiblePageTriggered);

const setupIntersectionObserver = (pageContainer, context) => {
    const pages = context.querySelectorAll(pageContainer);
    if (pages) pages.forEach(page => addNewObservedElement(page));
};

function addNewObservedElement(element) {
    observer.observe(element);
    observeVisiblePage.observe(element);
}

function intersectionObserverTriggered(entries) {
    intersectionObserverTriggeredHandler(entries, 'js-pagination', (element) => {
        if (element.classList.contains('js-pagination')) observedPaginationScrolledInToView(element);
    });
}
function intersectionObserverVisiblePageTriggered(entries) {
    intersectionObserverTriggeredHandler(entries, 'product-grid', (element) => { if (element.getAttribute('data-page')) observedPageScrolledInToView(element); });
}

function intersectionObserverTriggeredHandler(entries, triggeredByClassName, cb) {
    entries.forEach(entry => {
        let halfScreenHeight = window.innerHeight / 2,
            entryHeight = entry.intersectionRect.height,
            element = entry.target;
        if (entryHeight > halfScreenHeight || entry.isIntersecting) cb(element);
    });
}

function observedPageScrolledInToView(element) {
    let pageNumber = parseInt(element.getAttribute('data-page'));
    if (window.location.href.includes('page=') || pageNumber !== 1) {
        updateUrl({
            page: pageNumber
        });
    }
}
function observedPaginationScrolledInToView(element) {
    element.querySelectorAll('a')[0]?.click();
    element.querySelectorAll('a')[0]?.remove();
}

// #endregion

// #region Capture clicks
export const setupCaptureClicks = (context, watch, ignore = '.no-ajax') => {
    getElementsToCaptureClicks(context, watch).then((elements) => applyEventListeners(elements, ignore));
};

const getElementsToCaptureClicks = (context, classNames) => {
    return new Promise((resolve) => {
        const elementsToReturn = [];
        const elements = context.querySelectorAll(classNames.join(','));
        elements.forEach((e) => {
            if (!e.classList.contains('js-toggle-close')) elementsToReturn.push(e);
        });
        resolve(elementsToReturn);
    });
};

const applyEventListeners = (elements, ignore) => {
    elements.forEach((element) => {
        element.addEventListener('click', (e) => {
            let urlElement = e.target.closest(`[href]:not(${ignore})`);
            if (urlElement) {
                let url = getAttributeOrData(urlElement, 'href');
                let filterLink = urlElement.closest('.filters-draw') === null ? false : true;
                let categoryLink = urlElement.closest('.category-links') === null ? false : true;

                if (!categoryLink) {
                    e.preventDefault();
                    history.replaceState(null, "Loading...", url);
                    updateHistoryState(url, DEFAULTS, filterLink);
                }
            }
        });
    });
};


// #endregion

export const pageLoader = (opts) => {

    if (document.body.classList.contains('ie-fix')) {
        document.querySelectorAll('.paging-container').forEach(e => e.classList.remove('d-none'));
        return;
    }

    const options = Object.assign(DEFAULTS, opts);
    const context = document.querySelector(options.context);
    if (!context) throw Error("page-loader: context not found");

    if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
    }

    setupCaptureClicks(context, options.watch, options.ignore);

    if (options.saveScrollPosiion) handleScrollPosition(context);
    if (options.pagingHandler) options.pagingHandler.call(this, null, context, options);

    watchHistoryPopState(() => triggerPageUpdate('', "", false));

    goToProduct(options);

    window.addEventListener("pageshow", function (event) {
        var historyTraversal = event.persisted || (typeof window.performance != "undefined" && window.performance.navigation.type === 2);
        if (historyTraversal) goToProduct(options);
    });
};

function getAttributeOrData(element, attribute) {
    return element.getAttribute(`data-${attribute}`) ? element.getAttribute(`data-${attribute}`) : element.getAttribute(attribute);
}

export const updateHistoryState = (url, options = DEFAULTS, isFilterLink) => triggerPageUpdate(url, options = DEFAULTS, isFilterLink);

const watchHistoryPopState = (cb) => window.addEventListener('popstate', cb);

const handleScrollPosition = (context, options = DEFAULTS) => {
    setupIntersectionObserver(options.pageContainer, context);
    options.setupScrollClickHandler();
};

const goToProduct = (options = DEFAULTS) => {
    let scrollToProductId = getScrollToProductId();
    if (sessionStorage.scrollTo || scrollToProductId) {
        let product;
        if (sessionStorage.scrollTo) product = document.querySelector(`.associated-product[data-pid="${sessionStorage.scrollTo}"]`);
        else product = document.querySelector(`${options.item}[data-pid="${scrollToProductId}"]`);
        if (product) {
            removeScrollToProductId();
            let header = document.querySelector('.header-sticky');
            let top = getOffset(product).top;
            let offset = isMobile() ? 110 : 120;
            top = top - offset;
            window.scroll({ top: top });
            document.body.classList.remove('headroom-pinned');
            header.classList.remove('headroom--pinned');
            header.classList.add('headroom--unpinned');
        }
    }
};

const triggerPageUpdate = async function (url, options = DEFAULTS, isFilterLink) {
    if (!url) return;
    const context = document.querySelector(options.paging.container);
    panelUpdater(context, processPage(url, options, isFilterLink));
};

const processPage = (url, options = DEFAULTS, isFilterLink) => {
    const context = document.querySelector(options.context);

    return new Promise((resolve) => {
        updatePageTitle("Loading...");
        fetchPageFragment(url, options)
            .then((fragment) => checkServiceWorker(fragment, options))
            .then((fragment) => callModifyContent(fragment, options.modifyContent))
            .then((fragment) => callBeforeUpdate(fragment, options.beforeUpdate))
            .then((fragment) => callUpdatePage(fragment, context, options, isFilterLink))
            .then((fragment) => callPagingHandler(fragment, context, options))
            .then((fragment) => callAfterUpdate(fragment, options.afterUpdate))
            .catch((e) => {
                console.log('processPage: Error caught', e);
                if (e.cause?.name === 'Offline') return;
                window.location.reload()
            })
            .finally(() => {
                document.querySelectorAll('.js-pending-delete').forEach(e => e.remove());
                triggerPageViewEvent(context, url);
                resolve('');
            });
    });
};

function checkServiceWorker(fragment: DocumentFragment, options: PageLoaderOptions) {
    if (fragment.querySelector('.js-plp-fallback')) {
        updatePageSW(fragment, options);
        // this is to break out of the promise chain in processPage
        throw Error('Service Worker Fallback Detected', { cause: { name: 'Offline', message: 'Service Worker Fallback' } });
    }
    return fragment;
}

function updatePageSW(fragment, options) {
    return new Promise((resolve) => {
        // append the fallback content to the page
        const fragmentInner = fragment.querySelector('.js-plp-fallback');
        const containers = Array(...document.querySelectorAll(options.pageContainer));
        const container = containers.slice(-1)[0];
        container.parentNode.insertBefore(fragmentInner, container.nextSibling);
        const paginationContainer = document.querySelector('.js-pagination');
        paginationContainer.classList.add('d-none');
        resolve(fragment);
    });
}

function callModifyContent(fragment, cb) {
    return cb ? cb(fragment) : fragment;
}

function callPagingHandler(fragment, context, options) {
    return options.pagingHandler ? options.pagingHandler.call(this, fragment, context, options) : fragment;
}

function callBeforeUpdate(fragment, cb) {
    return cb ? cb(fragment) : fragment;
}

function callUpdatePage(fragment, context, options, isFilterLink) {
    return options.updateMethod.call(this, fragment, context, options, isFilterLink);
}

function callAfterUpdate(fragment, cb) {
    return cb ? cb(fragment) : fragment;
}

const fetchPageFragment = (url, options) => {
    if (!url) return;
    return fetchText(url, { cache: 'no-store' }).then((html) => document.createRange().createContextualFragment(html));
};

const scrollContextTop = (options = DEFAULTS) => {
    if (options.scrollToTop) {
        scrollIntoView(document.body);
    }
};

export const replacePageContent = (fragment, context = document, options = DEFAULTS) => {
    return new Promise((resolve) => {
        scrollContextTop(options);
        if (context instanceof HTMLElement) context.innerHTML = '';
        context.appendChild(fragment);
        updatePageTitle(fragment);
        resolve(fragment);
    });
};

export const appendPageContent = (fragment, context = document, options = DEFAULTS, isFilter) => {
    return new Promise((resolve) => {
        if (fragment.querySelector('.js-no-products')) resolve(fragment);
        const fragmentInner = fragment.querySelectorAll(options.pageContainer)[0];
        const containers = Array(...context.querySelectorAll(options.pageContainer));
        const newPage = fragment.querySelector(options.pageContainer).getAttribute('data-page') * 1;
        const firstPage = context.querySelector(options.pageContainer)?.getAttribute('data-page') * 1;
        const direction = newPage < firstPage ? 'prev' : 'next';
        const pagination = context.querySelectorAll('.js-pagination');

        let container;

        if (direction === 'prev') {
            container = containers[0];
            container.parentNode.insertBefore(fragmentInner, container);
            let currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            let newPageHeight = fragmentInner.clientHeight;
            currentScrollPosition = currentScrollPosition === 0 ? -80 : currentScrollPosition;
            window.scrollTo(0, currentScrollPosition + newPageHeight);
            addNewObservedElement(Array(...context.querySelectorAll(options.pageContainer))[0]);

        } else {
            container = containers.slice(-1)[0];
            container.parentNode.insertBefore(fragmentInner, container.nextSibling);
            addNewObservedElement(Array(...context.querySelectorAll(options.pageContainer)).slice(-1)[0]);
        }

        // if is a filter click remove the old products and pagination
        if (isFilter) {
            containers.forEach((container) => {
                container.remove();
            });

            pagination.forEach((container) => {
                container.remove();
            });

        }

        updatePageTitle(fragment);
        resolve(fragment);
    });
};

export const pagingForPrevNext = (fragment, context = document, options = DEFAULTS) => {
    const pagingContext = fragment ? fragment : context;
    const fragmentPagingContainer = pagingContext.querySelector(options.paging.container);
    if (!fragmentPagingContainer) return fragment;
    const nextButton = fragmentPagingContainer.querySelector(options.paging.next);
    const prevButton = fragmentPagingContainer.querySelector(options.paging.prev);
    const pagingContainerEl = pagingContext.querySelector(options.paging.container);
    const pagingContainer = pagingContainerEl.closest('.container');
    addPaginationButton(pagingContainer.cloneNode(true), prevButton, 'prepend', context, options);
    addPaginationButton(pagingContainer.cloneNode(true), nextButton, 'append', context, options);
    cleanPreviousPagination(options.pageContainer);
    return fragment;
};

const getVisiblePages = (pageContainer) => {
    return Array(...document.querySelectorAll(`${pageContainer}:not(.js-pending-delete)`)).map(x => x.getAttribute('data-page'));
};

const cleanPreviousPagination = (pageContainer) => {

    const pagination = document.querySelectorAll('.js-pagination');
    const pages = getVisiblePages(pageContainer);

    let link, page;
    if (pagination) {
        pagination.forEach(paginationItem => {
            link = paginationItem.querySelector('a');
            if (link) {
                link.classList.remove('panel-updater', 'is-loading');
                page = link.getAttribute('data-targetpage');
            }
            if (page) {
                if (pages.includes(page)) {
                    paginationItem.closest('.container').remove();
                }
            }
        });
    }
};

const addPaginationButton = (template, button, position, context = document, options = DEFAULTS) => {
    if (!button) return;
    //Don't add disabled pagination
    if (button.classList.contains('disabled')) return;
    //Don't add pagination for pages already visible
    const pages = getVisiblePages(options.pageContainer);
    let page = button.getAttribute('data-targetpage');

    if (pages.includes(page)) return;

    const templateInner = template.querySelector(options.paging.container);
    template.classList.remove('js-pending-delete', 'invisible');
    templateInner.innerHTML = "";
    templateInner.appendChild(button);
    const containers = Array(...context.querySelectorAll(options.pageContainer));
    const container = position === 'prepend' ? containers[0] : containers.slice(-1)[0];
    let target = position === 'prepend' ? container : document.querySelector('.js-page-prev')?.closest('.container.js-pagination');

    if (options.paging.insertNextAfter && position === 'append')
        target = context.querySelector(options.paging.insertNextAfter).nextElementSibling;

    if (!target.classList.contains('product-grid')) target.remove();
    if (position === "prepend") document.querySelector('.js-listing-page').prepend(template);
    else document.querySelector('.js-listing-page').appendChild(template);

    setupCaptureClicks(template, [`.${button.classList[0]}`]);

    if (options.activationMethod.next === 'scroll' && position === 'append') {
        template.classList.add('js-pagination', 'invisible');
        window.setTimeout(() => addNewObservedElement(template), 1);
    }

    if (options.activationMethod.previous === 'scroll' && position === 'prepend') {
        template.classList.add('js-pagination');
        window.setTimeout(() => addNewObservedElement(template), 1);
    }
};

const updatePageTitle = (pageTitle, context = document) => {
    document.title = typeof pageTitle === "string" ? pageTitle : getPageTitleFromDOM(context);
};

const getPageTitleFromDOM = (context) => {
    const pageTitleElement = context.querySelectorAll('input[name="ajaxTitle"]');
    return pageTitleElement ? [].slice.call(pageTitleElement).pop().value : "";
};

const triggerPageViewEvent = (context, url) => {
    dispatchEventWrapper(context, 'rc-pageview', {
        type: 'pageview',
        url: url,
        title: getPageTitleFromDOM(document)
    });
};

const DEFAULTS = {
    context: '.js-listing-page',
    watch: [
        '.filters-draw',
        '.paging-container',
        '.js-pagination',
        '.js-apply-price-filter'
    ],
    filters: '.filters-draw',
    pageContainer: '.product-grid',
    item: '.associated-product',
    paging: {
        container: '.paging-container',
        next: '.js-page-next',
        prev: '.js-page-prev',
        insertNextAfter: false
    },
    activeClass: '.is-visible',
    ignore: '.no-ajax',
    method: 'replace',
    scrollToTop: true,
    saveScrollPosiion: true,
    pagingHandler: false,
    modifyContent: false,
    updateMethod: replacePageContent,
    beforeUpdate: false,
    afterUpdate: false,
    setupScrollClickHandler: false,
    activationMethod: {
        next: 'scroll',
        previous: 'scroll'
    }
};